import { nanoid } from '@reduxjs/toolkit';
import type { DefaultAxesParam } from '@/features/chartsView';
import { ChartType } from '@/features/chartsView';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type { SerializedDateRangeValueObject } from '@/shared/types';
import { DateRangePresets, DateScale } from '@/shared/types';
import * as CalendarChartAPI from '../api/calendarChartAPI';
import type {
  CalendarChartConfig,
  CalendarDataResponse,
  ChartMeta,
} from './types';

export interface State {
  meta:
    | {
        data: ChartMeta;
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  chart: CalendarChartConfig | null;
  isConfigInitialized: boolean;
}

const initialState: State = {
  meta: {
    data: null,
    status: DataStatus.idle,
  },
  chart: null,
  isConfigInitialized: false,
};

export const calendarChartSlice = createAppSlice({
  name: 'calendarChart',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CalendarChartAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;
            const { status } = state.calendarChart.meta;

            if (isLoading(status) || isFinished(status)) {
              return false;
            }
          },
        },
      },
    ),
    updateCohortDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.cohortDateScale = action.payload;
    }),
    updateCohortDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.cohortDateRange = action.payload;
      },
    ),
    updateRevenueDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.revenueDateScale = action.payload;
    }),
    updateRevenueDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        state.chart.revenueDateRange = action.payload;
      },
    ),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.probabilisticAttribution = action.payload;
    }),
    updateSplitDimension: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.splitDimension = action.payload;
    }),
    updatePrimaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.primaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric].type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateSecondaryMetric: create.reducer<string>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.secondaryMetric = action.payload;

      if (
        state.meta.data?.config[state.chart.primaryMetric].type !==
        state.meta.data?.config[state.chart.secondaryMetric]?.type
      ) {
        state.chart.syncedYAxes = false;
      }
    }),
    updateExcludedSplitOptions: create.reducer<string[]>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.excludedSplitOptions = action.payload;
    }),
    updateSyncedYAxes: create.reducer<boolean>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.syncedYAxes = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.chart) {
        return;
      }

      state.chart.filters = action.payload;
    }),
    updateChart: create.reducer<CalendarChartConfig>((state, action) => {
      state.chart = action.payload;
      state.isConfigInitialized = true;
    }),
    resetChart: create.reducer((state) => {
      if (!isMetaFinished(state.meta)) {
        state.chart = null;

        return;
      }

      state.chart = {
        id: nanoid(),
        ...getDefaultChartProps(state.meta.data.defaultAxis),
      };
      state.isConfigInitialized = true;
    }),
    resetChartWithOverride: create.reducer<Partial<CalendarChartConfig>>(
      (state, action) => {
        if (!isMetaFinished(state.meta)) {
          state.chart = null;

          return;
        }

        state.chart = {
          id: nanoid(),
          ...getDefaultChartProps(state.meta.data.defaultAxis),
          ...action.payload,
        };
        state.isConfigInitialized = true;
      },
    ),
    setupDefaultExcludedSplitOptions: create.reducer<string[]>(
      (state, action) => {
        if (!state.chart) {
          return;
        }

        const optionsToExclude = action.payload.slice(3);

        state.chart.excludedSplitOptions = optionsToExclude;
      },
    ),
    toggleExcludedSplitOptions: create.reducer<{
      options: string[];
      checked: boolean;
    }>((state, action) => {
      if (!state.chart) {
        return;
      }

      const excludedOptionsSet = new Set(state.chart.excludedSplitOptions);

      if (!action.payload.checked) {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.add(option);
        });
      } else {
        action.payload.options.forEach((option) => {
          excludedOptionsSet.delete(option);
        });
      }

      state.chart.excludedSplitOptions = Array.from(excludedOptionsSet);
    }),
  }),
  selectors: {
    selectChart: (state) => state.chart,
    selectIsConfigInitialized: (state) => state.isConfigInitialized,
    selectMeta: (state) => state.meta.data,
    selectMetaDataStatus: (state) => state.meta.status,
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
    selectMetaIsLoading: (state) => isLoading(state.meta.status),
  },
});

const getDefaultChartProps = (
  defaultAxes: DefaultAxesParam,
): Omit<CalendarChartConfig, 'id'> => {
  return {
    type: ChartType.calendar,
    excludedSplitOptions: [],
    syncedYAxes: true,
    filters: [],
    cohortDateScale: DateScale.DAY,
    cohortDateRange: { preset: DateRangePresets.ALL_TIME },
    revenueDateScale: DateScale.DAY,
    revenueDateRange: { preset: DateRangePresets.LAST_90_DAYS },
    probabilisticAttribution: true,
    splitDimension: defaultAxes.dimension,
    primaryMetric: defaultAxes.primaryMetric,
    secondaryMetric: defaultAxes.secondaryMetric,
  };
};

function isMetaFinished(meta: State['meta']): meta is {
  data: ChartMeta;
  status: DataStatus.finished;
} {
  return meta.status === DataStatus.finished;
}

export const getChartData =
  (chart: CalendarChartConfig): AppThunk<Promise<CalendarDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState() as RootState;

    if (!isFinished(state.calendarChart.meta.status)) {
      return Promise.reject(new Error('Meta not initialized'));
    }

    const { meta } = state.calendarChart;
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      chart.filters,
      meta.data!.metrics,
      meta.data!.dimensions,
    );

    return CalendarChartAPI.getCalendarData({
      split: chart.splitDimension,
      metrics: [chart.primaryMetric, chart.secondaryMetric].filter(Boolean),
      cohortDateScale: chart.cohortDateScale,
      cohortDateRange: prepareDateRangeValueObject(chart.cohortDateRange),
      revenueDateScale: chart.revenueDateScale,
      revenueDateRange: prepareDateRangeValueObject(chart.revenueDateRange),
      probabilisticAttribution: chart.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
    });
  };

export const {
  initMeta,
  updateCohortDateScale,
  updateCohortDateRange,
  updateRevenueDateScale,
  updateRevenueDateRange,
  updateProbabilisticAttribution,
  updateSplitDimension,
  updatePrimaryMetric,
  updateSecondaryMetric,
  updateExcludedSplitOptions,
  updateSyncedYAxes,
  updateFilters,
  updateChart,
  resetChart,
  resetChartWithOverride,
  setupDefaultExcludedSplitOptions,
  toggleExcludedSplitOptions,
} = calendarChartSlice.actions;
export const {
  selectChart,
  selectIsConfigInitialized,
  selectMeta,
  selectMetaDataStatus,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} = calendarChartSlice.selectors;
